import React from 'react';
import * as Yup from 'yup';
import { Divider, Stack } from '@mui/material';
import { isDefined, isEmptyString } from 'lib/typeguards';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { CreatableChipInputFormField } from 'lib/form/fields/CreatableChipInputFormField';
import logger from 'lib/logger';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getPublicAndPrivateSettings } from 'store/settings/settingsSlice';
import { EditableSettingsSection } from './EditableSettingsSection';
import { FieldStatusRow, LabeledField } from './SetupSectionPrimitives';

interface FormValues {
    teamId: string;
    sha256CertFingerprints: string[];
    appUrlScheme: string;
}

interface AppLinksConfigurationSectionProps {
    isReadOnly?: boolean;
}

const isSet = (value?: string) => isDefined(value) && !isEmptyString(value);

const validationSchema = Yup.object().shape({
    teamId: Yup.string().trim().required('Apple Team ID is required.'),
    sha256CertFingerprints: Yup.array()
        .of(Yup.string().trim().required())
        .min(1, 'At least one SHA-256 fingerprint is required.'),
    appUrlScheme: Yup.string().trim().required('App URL Scheme is required.')
});

export const AppLinksConfigurationSection: React.FC<AppLinksConfigurationSectionProps> = ({
    isReadOnly = false
}) => {
    const settings = useAppSelector(state => state.settings.settings);
    const { claims } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();

    const teamId = settings?.app?.appleTeamId;
    const fingerprints = React.useMemo(
        () => settings?.app?.androidSHA256CertFingerprints ?? [],
        [settings?.app?.androidSHA256CertFingerprints]
    );
    const appUrlScheme = settings?.app?.appURLScheme;

    const hasTeamId = isSet(teamId);
    const hasFingerprints = fingerprints.length > 0;
    const hasAppUrlScheme = isSet(appUrlScheme);
    const isConfigured = hasTeamId && hasFingerprints && hasAppUrlScheme;

    const initialValues = React.useMemo<FormValues>(
        () => ({
            teamId: teamId ?? '',
            sha256CertFingerprints: fingerprints,
            appUrlScheme: appUrlScheme ?? ''
        }),
        [teamId, fingerprints, appUrlScheme]
    );

    const handleSave = React.useCallback(
        async (values: FormValues) => {
            try {
                await new LocationSettingsApi().updatePublicAndPrivateSettings(
                    {
                        app: {
                            androidSHA256CertFingerprints: values.sha256CertFingerprints,
                            appURLScheme: values.appUrlScheme,
                            appleTeamId: values.teamId
                        }
                    },
                    undefined,
                    true
                );
                dispatch(getPublicAndPrivateSettings(claims));
                return true;
            } catch (error) {
                logger.error(`Error updating app links configuration: ${JSON.stringify(error)}`);
                return false;
            }
        },
        [claims, dispatch]
    );

    const renderFields = React.useCallback(
        (isEditing: boolean) => (
            <Stack spacing={2}>
                <LabeledField label="App URL Scheme" buildTime>
                    <TextFormField
                        name="appUrlScheme"
                        disabled={!isEditing}
                        placeholder="—"
                        showHelperText={isEditing}
                    />
                </LabeledField>
                <LabeledField label="Apple Team ID" buildTime>
                    <TextFormField
                        name="teamId"
                        disabled={!isEditing}
                        placeholder="—"
                        showHelperText={isEditing}
                    />
                </LabeledField>
                <LabeledField label="Android SHA-256 certificate fingerprints" buildTime>
                    <CreatableChipInputFormField
                        name="sha256CertFingerprints"
                        disabled={!isEditing}
                        placeholder="—"
                        description={isEditing ? 'Add one fingerprint per chip.' : undefined}
                    />
                </LabeledField>
            </Stack>
        ),
        []
    );

    const renderReadOnlyRows = React.useCallback(
        () => (
            <Stack divider={<Divider />}>
                <FieldStatusRow label="App URL Scheme" isSet={hasAppUrlScheme} />
                <FieldStatusRow label="Apple Team ID" isSet={hasTeamId} />
                <FieldStatusRow label="Android SHA-256 fingerprints" isSet={hasFingerprints} />
            </Stack>
        ),
        [hasTeamId, hasFingerprints, hasAppUrlScheme]
    );

    return (
        <EditableSettingsSection<FormValues>
            title="App Links configuration"
            subtitle="Synced with settings. This is required to begin distributing app links."
            readOnlySubtitle="Managed by your Owner or Pepper Admin. Values are hidden."
            isConfigured={isConfigured}
            isReadOnly={isReadOnly}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSave={handleSave}
            renderFields={renderFields}
            renderReadOnlyRows={renderReadOnlyRows}
        />
    );
};
